import React from "react";
import { Box, Button, Typography } from "@mui/material";

const PAD = 8;
const TOOLTIP_W = 340;
const GREEN = "#56ECCF";

/**
 * Spotlight-style page tour.
 * steps: [{ id, title, body, targetRef?, center? }]
 */
export default function PageTutorial({
  open,
  steps = [],
  stepIndex = 0,
  onContinue,
  onSkip,
  skippable = true,
  continueEnabled = true,
  continueLabel = null,
  showContinue = true,
}) {
  const step = open ? steps[stepIndex] : null;
  const [rect, setRect] = React.useState(null);
  const total = steps.length;
  const isCenter = !!(step?.center || (!step?.targetRef && step));

  const measure = React.useCallback(() => {
    if (isCenter) {
      setRect(null);
      return;
    }
    const el = step?.targetRef?.current;
    if (!el || typeof el.getBoundingClientRect !== "function") {
      setRect(null);
      return;
    }
    try {
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    } catch (_) {}
    const r = el.getBoundingClientRect();
    if (!r || r.width < 2 || r.height < 2) {
      setRect(null);
      return;
    }
    setRect({
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
    });
  }, [step, isCenter]);

  React.useEffect(() => {
    if (!open || !step) {
      setRect(null);
      return undefined;
    }
    measure();
    const onWin = () => measure();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    const id = window.setInterval(measure, 400);
    const t1 = window.setTimeout(measure, 120);
    const t2 = window.setTimeout(measure, 360);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
      window.clearInterval(id);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [open, step, stepIndex, measure]);

  if (!open || !step) return null;

  const isLast = stepIndex >= total - 1;
  const highlight = !isCenter && rect
    ? {
        top: Math.max(8, rect.top - PAD),
        left: Math.max(8, rect.left - PAD),
        width: Math.min(window.innerWidth - 16, rect.width + PAD * 2),
        height: Math.min(window.innerHeight - 16, rect.height + PAD * 2),
      }
    : null;

  let tipTop = Math.max(24, Math.round(window.innerHeight / 2 - 120));
  let tipLeft = Math.max(16, Math.round((window.innerWidth - TOOLTIP_W) / 2));
  if (highlight) {
    const below = highlight.top + highlight.height + 12;
    const above = highlight.top - 12;
    const tipH = 200;
    const preferBelow = below + tipH < window.innerHeight - 16;
    tipTop = preferBelow ? below : Math.max(16, above - tipH);
    tipLeft = Math.min(
      Math.max(16, highlight.left),
      Math.max(16, window.innerWidth - TOOLTIP_W - 16)
    );
  }

  const canContinue = continueEnabled !== false;
  const primaryLabel = continueLabel || (isLast ? "Got it" : "Continue");

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 2400,
        pointerEvents: "none",
        "@keyframes pmTutorialPulse": {
          "0%, 100%": {
            boxShadow:
              "0 0 0 2px rgba(86,236,207,0.95), 0 0 22px rgba(86,236,207,0.35), 0 0 0 9999px rgba(0,0,0,0.62)",
          },
          "50%": {
            boxShadow:
              "0 0 0 3px rgba(86,236,207,1), 0 0 34px rgba(86,236,207,0.55), 0 0 0 9999px rgba(0,0,0,0.62)",
          },
        },
      }}
    >
      {(isCenter || !highlight) && (
        <Box sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.72)", pointerEvents: "auto" }} />
      )}

      {highlight && (
        <Box
          sx={{
            position: "fixed",
            top: highlight.top,
            left: highlight.left,
            width: highlight.width,
            height: highlight.height,
            borderRadius: "12px",
            pointerEvents: "none",
            animation: "pmTutorialPulse 1.6s ease-in-out infinite",
            border: `1px solid ${GREEN}`,
          }}
        />
      )}

      <Box
        sx={{
          position: "fixed",
          top: tipTop,
          left: tipLeft,
          width: TOOLTIP_W,
          maxWidth: "calc(100vw - 32px)",
          pointerEvents: "auto",
          backgroundColor: "#1f1f1f",
          border: `1px solid rgba(86,236,207,0.55)`,
          borderRadius: "12px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
          p: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: GREEN, letterSpacing: "0.04em", mb: 0.4 }}>
          {`STEP ${stepIndex + 1} OF ${total}`}
        </Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#fff", mb: 0.6, lineHeight: 1.25 }}>
          {step.title}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#c8c8c8", lineHeight: 1.45, mb: 1.4 }}>
          {step.body}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.8, justifyContent: "flex-end", alignItems: "center" }}>
          {skippable && typeof onSkip === "function" && (
            <Button
              size="small"
              onClick={onSkip}
              sx={{
                textTransform: "none",
                color: "#8f8f8f",
                fontSize: 12,
                fontWeight: 600,
                "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.06)" },
              }}
            >
              Skip tutorial
            </Button>
          )}
          {showContinue !== false && (
            <Button
              size="small"
              variant="contained"
              disabled={!canContinue}
              onClick={onContinue}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                fontSize: 13,
                px: 1.6,
                backgroundColor: GREEN,
                color: "#111",
                "&:hover": { backgroundColor: "#45b39b" },
                "&.Mui-disabled": { backgroundColor: "#3a3a3a", color: "#777" },
              }}
            >
              {primaryLabel}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export const SOLVER_TUTORIAL_STORAGE_KEY = "pm_tutorial_done_solver_v4";

export function hasCompletedTutorial(storageKey) {
  try {
    return localStorage.getItem(storageKey) === "1";
  } catch (_) {
    return false;
  }
}

export function markTutorialComplete(storageKey) {
  try {
    localStorage.setItem(storageKey, "1");
  } catch (_) {}
}
